/**
 * SideMenu.tsx - Side Navigation Menu Component
 * 
 * Universal sidebar navigation with full OpCentral orchestrator integration
 * Migrated from Interfacr SideMenu with enhancements
 * 
 * FEATURES:
 * - Left/right sidebar navigation
 * - Collapsible sidebar
 * - Categories/sections support
 * - Drag and drop reordering (Draggr)
 * - State persistence (Cachr)
 * - Action logging (Actr)
 * - Undo/redo support (Backtrackr)
 * - Plugin items integration
 * - Skinnr/Stylr theming
 * - Search functionality (optional)
 * 
 * ARCHITECTURE:
 * - Uses MenuItem and MenuSection components
 * - Uses useMenuState hook (Cachr/Actr/Backtrackr integrated)
 * - Uses MenuItemManager utility
 * - Uses @opcentral/draggr for drag and drop
 * 
 * CHANGE HISTORY:
 * - 251115_1720 [current] danielbeer: Created with orchestrator integration
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMenuState } from './useMenuState';
import { MenuItemManager } from './MenuItemManager';
import { MenuItem } from './MenuItem';
import { MenuSection } from './MenuSection';
import type { SideMenuProps, MenuItem as MenuItemType } from './types';
import './Menu.css';

export const SideMenu: React.FC<SideMenuProps> = ({
  appName,
  items,
  theme = 'dark',
  variant = 'default',
  collapsible = true,
  defaultCollapsed = false,
  categories = false,
  persistState = true,
  search = false,
  badges: _badges = true, // TODO: Pass to MenuItem to control badge visibility
  shortcuts: _shortcuts = true, // TODO: Pass to MenuItem to control shortcut visibility
  draggable = false,
  onItemClick,
  onStateChange,
  onItemReorder,
  className = ''
}) => {
  // Menu state management (already has Cachr/Actr/Backtrackr)
  const {
    state,
    toggleCollapsed,
    toggleItemExpanded,
    selectItem,
    reorderItems,
    setSearchQuery,
    clearSearch
  } = useMenuState({
    appName,
    menuId: 'side-menu',
    persistState,
    defaultCollapsed,
    onStateChange
  });
  
  // Menu item manager
  const itemManager = useMemo(() => new MenuItemManager(items), [items]);
  
  // Search state
  const [searchInput, setSearchInput] = useState('');
  
  // Update search query in state
  useEffect(() => {
    if (search && searchInput) {
      setSearchQuery(searchInput);
    } else if (!searchInput) {
      clearSearch();
    }
  }, [searchInput, search, setSearchQuery, clearSearch]);
  
  // Get filtered items based on search
  const filteredItems = useMemo(() => {
    if (!search || !state.searchQuery) {
      return itemManager.getVisibleItems();
    }
    return itemManager.search(state.searchQuery);
  }, [itemManager, search, state.searchQuery]);
  
  // Group items by category if enabled
  const groupedItems = useMemo(() => {
    if (!categories) {
      return { '': filteredItems };
    }
    
    const groups: Record<string, MenuItemType[]> = {};
    const categoryList = itemManager.getCategories();
    
    categoryList.forEach(category => {
      const categoryItems = filteredItems.filter(item => item.category === category);
      if (categoryItems.length > 0) {
        groups[category] = categoryItems;
      }
    });
    
    // Add uncategorized items
    const uncategorized = filteredItems.filter(item => !item.category);
    if (uncategorized.length > 0) {
      groups[''] = uncategorized;
    }
    
    return groups;
  }, [itemManager, categories, filteredItems]);
  
  // Handle item click
  const handleItemClick = useCallback((item: MenuItemType) => {
    selectItem(item.id);
    
    if (item.type === 'submenu') {
      toggleItemExpanded(item.id);
    }
    
    onItemClick?.(item);
  }, [selectItem, toggleItemExpanded, onItemClick]);
  
  // Handle reorder
  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    const allItems = filteredItems;
    const itemIds = allItems.map(i => i.id);
    reorderItems(fromIndex, toIndex, itemIds);
    onItemReorder?.(fromIndex, toIndex);
  }, [filteredItems, reorderItems, onItemReorder]);
  
  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);
  
  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSearchInput('');
    clearSearch();
  }, [clearSearch]);
  
  // Menu classes
  const menuClasses = useMemo(() => [
    'side-menu',
    `side-menu--${theme}`,
    `side-menu--${variant}`,
    state.collapsed ? 'side-menu--collapsed' : '',
    draggable ? 'side-menu--draggable' : '',
    className
  ].filter(Boolean).join(' '), [theme, variant, state.collapsed, draggable, className]);
  
  return (
    <nav 
      className={menuClasses}
      data-menu-id="side-menu"
      aria-label="Side navigation"
      role="navigation"
    >
      {/* Collapse toggle */}
      {collapsible && (
        <button
          className="side-menu__toggle"
          onClick={toggleCollapsed}
          title={state.collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-label={state.collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-expanded={!state.collapsed}
        >
          {state.collapsed ? '▸' : '◂'}
        </button>
      )}
      
      {/* Search bar */}
      {search && !state.collapsed && (
        <div className="side-menu__search">
          <input
            type="text"
            className="side-menu__search-input"
            placeholder="Search menu..."
            value={searchInput}
            onChange={handleSearchChange}
            aria-label="Search menu items"
          />
          {searchInput && (
            <button
              className="side-menu__search-clear"
              onClick={handleSearchClear}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      )}
      
      {/* Menu content */}
      <div className="side-menu__content">
        {categories ? (
          // Render categorized items
          Object.entries(groupedItems).map(([category, categoryItems]) => (
            <MenuSection
              key={category || 'uncategorized'}
              title={category}
              items={categoryItems}
              collapsed={state.collapsed}
              expandedItems={state.expandedItems}
              selectedItem={state.selectedItem}
              draggable={draggable}
              onItemClick={handleItemClick}
              onToggleExpanded={toggleItemExpanded}
              onItemReorder={handleReorder}
            />
          ))
        ) : (
          // Render flat list
          <div className="side-menu__items" role="menu">
            {filteredItems.map((item, index) => (
              <MenuItem
                key={item.id}
                item={item}
                index={index}
                collapsed={state.collapsed}
                selected={state.selectedItem === item.id}
                expanded={state.expandedItems.includes(item.id)}
                draggable={draggable}
                onClick={() => handleItemClick(item)}
                onToggleExpanded={() => toggleItemExpanded(item.id)}
                onReorder={handleReorder}
              />
            ))}
          </div>
        )}
        
        {/* Empty state */}
        {filteredItems.length === 0 && (
          <div className="side-menu__empty">
            {state.searchQuery ? (
              <p>No items match "{state.searchQuery}"</p>
            ) : (
              <p>No menu items</p>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

SideMenu.displayName = 'SideMenu';
